import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';

/** Default events fetched per page. */
const PAGE_SIZE = 50;

/**
 * Max authors per filter. Relays choke on large `authors` filters: live
 * probing showed a 250-author kind-1 filter never gets an EOSE (0 events
 * after the full timeout) on relay.ditto.pub, relay.primal.net,
 * relay.damus.io and nos.lol, while 100-author filters answer in <1s on all
 * of them. Keep this at 100.
 */
export const AUTHORS_PER_FILTER = 100;

export interface TimelinePage {
  events: NostrEvent[];
  /** `until` cursor for the next page, or null when the timeline is exhausted. */
  nextUntil: number | null;
}

export function chunkAuthors(authors: string[]): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < authors.length; i += AUTHORS_PER_FILTER) {
    chunks.push(authors.slice(i, i + AUTHORS_PER_FILTER));
  }
  return chunks;
}

/**
 * Sort fetched events and derive the next `until` cursor.
 *
 * Single filter: more pages exist iff the page filled up; cursor is the
 * oldest event. Chunked filters apply `limit` independently, so the safe
 * cursor is the *newest* of the per-chunk minima among chunks that filled
 * their limit — events older than it (from quieter chunks) are trimmed,
 * otherwise the busy chunk's events between the two minima would be skipped.
 */
export function assembleTimelinePage(
  fetched: NostrEvent[],
  chunks: string[][],
  pageSize: number,
): TimelinePage {
  let events = [...fetched].sort((a, b) => b.created_at - a.created_at);

  let nextUntil: number | null = null;
  if (events.length > 0) {
    if (chunks.length > 1) {
      let cursor: number | null = null;
      for (const c of chunks) {
        const set = new Set(c);
        const chunkEvents = events.filter((e) => set.has(e.pubkey));
        if (chunkEvents.length >= pageSize) {
          const oldest = chunkEvents[chunkEvents.length - 1].created_at;
          cursor = cursor === null ? oldest : Math.max(cursor, oldest);
        }
      }
      if (cursor !== null) {
        events = events.filter((e) => e.created_at >= cursor);
        nextUntil = cursor - 1;
      }
    } else if (events.length >= pageSize) {
      nextUntil = events[events.length - 1].created_at - 1;
    }
  }

  return { events, nextUntil };
}

/**
 * Reverse-chronological, `until`-cursor pagination over a Nostr filter.
 *
 * When `authors` exceeds the per-filter cap (100 — see AUTHORS_PER_FILTER) it
 * is split across multiple chunks and each chunk is queried as its OWN REQ,
 * fired in parallel. Relays silently drop/stall REQs whose filters carry
 * hundreds of authors, so many small REQs are used instead of one giant one.
 *
 * Because each chunk applies `limit` independently, the next-page cursor must
 * be the *newest* of the per-chunk minima among chunks that filled their
 * limit — anything older from other chunks is trimmed from the page,
 * otherwise events between the cursors would be skipped forever.
 *
 * An optional `fallbackRelays` set can be supplied to query a second group of
 * relays in parallel (e.g. Ditto) and merge the results. This is useful for
 * feeds that may be empty on the user's own relays but are indexed by a
 * broader relay.
 */
export function useInfiniteTimeline({
  queryKey,
  filter,
  authors,
  relays,
  fallbackRelays,
  enabled = true,
  pageSize = PAGE_SIZE,
  staleTime = 30000,
}: {
  queryKey: readonly unknown[];
  filter: Omit<NostrFilter, 'authors' | 'until' | 'limit'>;
  authors?: string[];
  relays?: string[];
  fallbackRelays?: string[];
  enabled?: boolean;
  pageSize?: number;
  staleTime?: number;
}) {
  const { nostr } = useNostr();

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }): Promise<TimelinePage> => {
      const base: NostrFilter = { ...filter };
      if (pageParam) base.until = pageParam;
      const chunks = authors ? chunkAuthors(authors) : [];

      // Spread the page size across chunks (floor of 15 so a quiet chunk still
      // contributes). A single chunk keeps the full page size, preserving the
      // simple single-filter behavior.
      const perChunkLimit = chunks.length > 1
        ? Math.max(15, Math.ceil(pageSize / chunks.length))
        : pageSize;

      // One filter list per chunk — each becomes its own REQ/subscription.
      const filterLists: NostrFilter[][] = chunks.length > 0
        ? chunks.map((c) => [{ ...base, limit: perChunkLimit, authors: c }])
        : [[{ ...base, limit: pageSize }]];

      // Fire every chunk query in parallel. A failing chunk (dead relay,
      // CLOSED sub) degrades to an empty result instead of killing the page.
      const queryAll = (targetRelays?: string[]) =>
        Promise.all(
          filterLists.map((filters) =>
            nostr.query(filters, {
              // Combine TanStack Query's cancellation signal with a hard
              // ceiling so slow/unresponsive relays can't stall the timeline.
              signal: AbortSignal.any([signal, AbortSignal.timeout(15000)]),
              ...(targetRelays ? { relays: targetRelays } : {}),
            }).catch(() => [] as NostrEvent[]),
          ),
        );

      const [primaryResults, fallbackResults] = await Promise.all([
        queryAll(relays),
        fallbackRelays ? queryAll(fallbackRelays) : Promise.resolve([] as NostrEvent[][]),
      ]);

      // Deduplicate events from all chunk/relay queries before assembling.
      const seen = new Set<string>();
      const fetched: NostrEvent[] = [];
      for (const results of [...primaryResults, ...fallbackResults]) {
        for (const event of results) {
          if (!seen.has(event.id)) {
            seen.add(event.id);
            fetched.push(event);
          }
        }
      }

      return assembleTimelinePage(fetched, chunks, perChunkLimit);
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.nextUntil ?? undefined,
    enabled,
    staleTime,
  });

  // Flatten pages, dropping cross-page duplicates (relays with skewed limits
  // can return the same event around a cursor boundary).
  const events = useMemo(() => {
    const seen = new Set<string>();
    const flat: NostrEvent[] = [];
    for (const page of query.data?.pages ?? []) {
      for (const event of page.events) {
        if (!seen.has(event.id)) {
          seen.add(event.id);
          flat.push(event);
        }
      }
    }
    return flat;
  }, [query.data]);

  return { ...query, events };
}
